import React, { FC, ReactElement } from 'react';
import styled, { keyframes } from 'styled-components';
import Header from '../../components/Header';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const PageContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #000000;
  color: white;
  padding: 40px;
  position: relative;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
  z-index: 1;
`;

const IconContainer = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 20px 40px rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const ClockIcon = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid white;
  border-radius: 50%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 20px;
    background: white;
    transform: translate(-50%, -100%);
    transform-origin: bottom;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 15px;
    background: white;
    transform: translate(-50%, -100%) rotate(90deg);
    transform-origin: bottom;
  }
`;

const ApprovalText = styled.h1`
  font-size: clamp(32px, 5vw, 64px);
  font-weight: 700;
  text-align: center;
  margin: 0 0 24px 0;
  color: #ffffff;
  line-height: 1.2;
`;

const WaitText = styled.p`
  font-size: clamp(18px, 3vw, 28px);
  text-align: center;
  margin: 0 0 32px 0;
  color: #ffffff;
  font-weight: 400;
  line-height: 1.4;
`;

const StatusMessage = styled.div`
  font-size: clamp(16px, 2.5vw, 22px);
  text-align: center;
  padding: 16px 24px;
  background: #000000;
  border-radius: 12px;
  color: #ffd700;
  margin: 24px 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  font-weight: 500;
`;

const RetryButton = styled.button`
  background: #000000;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 12px;
  margin-top: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.1);
  min-width: 160px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #000000;
    box-shadow: 0 12px 35px rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #000000;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    color: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 40px;
`;

const Dot = styled.div<{ delay: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  animation: ${pulse} 1.5s infinite;
  animation-delay: ${(props) => props.delay}s;
`;

interface AwaitingApprovalProps {
  statusMessage?: string;
  onRetry?: () => Promise<void>;
  isRetrying?: boolean;
}

const AwaitingApproval: FC<AwaitingApprovalProps> = ({
  statusMessage,
  onRetry,
  isRetrying = false,
}): ReactElement => (
  <PageContainer>
    <Header />
    <ContentContainer>
      <IconContainer>
        <ClockIcon />
      </IconContainer>

      <ApprovalText>
        Review in Progress
      </ApprovalText>

      <WaitText>
        We&apos;re currently reviewing your installation.
        <br />
        This usually takes just a few minutes.
      </WaitText>

      {statusMessage && (
        <StatusMessage>{statusMessage}</StatusMessage>
      )}

      {onRetry && (
        <RetryButton
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? 'Checking...' : 'Check Status'}
        </RetryButton>
      )}

      <ProgressDots>
        <Dot delay={0} />
        <Dot delay={0.2} />
        <Dot delay={0.4} />
      </ProgressDots>
    </ContentContainer>
  </PageContainer>
);

export default AwaitingApproval;
